/**
 * デスクトップ常駐・透過プラットフォーム統合ファサード (DesktopAmbientService)
 * aidlc-docs/construction/desktop_ambient_platform/docs/component_architecture.md 準拠
 * 公開ポート IDesktopAmbientPort を完全実装
 */

import type { DesktopWindowStateDTO, IDesktopAmbientPort } from '../ports/types';
import type { IPlatformBridgeAdapter, WindowBounds } from './bridges/IPlatformBridgeAdapter';
import { NativePlatformBridge } from './NativePlatformBridge';
import { TransparentWindowManager, type WindowConfig } from './TransparentWindowManager';
import { InteractionPassthrough, type SnapCorner } from './InteractionPassthrough';

export interface DesktopAmbientServiceOptions {
  edition?: 'web_free' | 'pc_app_paid' | 'mobile_app_paid';
  adapter?: IPlatformBridgeAdapter;
  windowConfig?: WindowConfig;
}

export class DesktopAmbientService implements IDesktopAmbientPort {
  private readonly platformBridge: NativePlatformBridge;
  private readonly windowManager: TransparentWindowManager;
  private readonly interaction: InteractionPassthrough;

  constructor(options: DesktopAmbientServiceOptions = {}) {
    this.platformBridge = new NativePlatformBridge({
      adapter: options.adapter,
      edition: options.edition,
    });

    const activeAdapter = this.platformBridge.getAdapter();
    this.windowManager = new TransparentWindowManager(activeAdapter);
    this.interaction = new InteractionPassthrough(activeAdapter, this.windowManager);

    // 初期化（非同期のためエラーハンドリング付きで起動）
    this.windowManager.init(options.windowConfig).catch((err) => {
      console.warn('[DesktopAmbientService] Failed to initialize window manager:', err);
    });
  }

  /**
   * 常に最前面表示（ピン留め）を切り替え
   */
  public async setAlwaysOnTop(enable: boolean): Promise<void> {
    await this.windowManager.setAlwaysOnTop(enable);
  }

  /**
   * クリック透過（作業邪魔防止）モードを切り替え
   */
  public async setClickThrough(enable: boolean): Promise<void> {
    await this.interaction.setClickThrough(enable);
  }

  /**
   * 画面四隅へのスナップ配置
   */
  public async snapToCorner(corner: SnapCorner): Promise<void> {
    await this.interaction.snapToCorner(corner);
  }

  /**
   * 現在のウィンドウ状態を取得
   */
  public async getWindowState(): Promise<DesktopWindowStateDTO> {
    const bounds = await this.windowManager.getBounds();

    return {
      isTransparent: this.windowManager.isTransparent(),
      isAlwaysOnTop: this.windowManager.isAlwaysOnTop(),
      isClickThrough: this.interaction.isClickThrough(),
      bounds,
    };
  }

  /**
   * プラットフォームサポート可否の確認
   */
  public isSupported(): boolean {
    return this.platformBridge.getAdapter().isSupported();
  }

  /**
   * ウィンドウ境界の手動設定 (ユーティリティ)
   */
  public async setBounds(bounds: Partial<WindowBounds>): Promise<void> {
    await this.windowManager.setBounds(bounds);
  }

  /**
   * リソース解放・終了
   */
  public async destroy(): Promise<void> {
    this.interaction.destroy();
    await this.windowManager.destroy();
  }
}
